import { getAuthToken, logout } from "./auth";

const API_URL =
  import.meta.env.VITE_APP_API_URL ||
  `${window.location.protocol}//${window.location.hostname}:3004`;
const request = async <T>(path: string, options?: RequestInit): Promise<T> => {
  const response = await fetch(`${API_URL}${path}`, {
    ...options,
    credentials: "include",
    headers: {
      "content-type": "application/json",
      authorization: `Bearer ${getAuthToken() || ""}`,
      ...options?.headers,
    },
  });
  if (response.status === 401) {
    logout();
    window.location.assign(
      `/app/login?next=${encodeURIComponent(window.location.pathname)}`,
    );
  }
  if (!response.ok) {
    const payload = await response.json().catch(() => ({}));
    if (payload?.error === "email_api_key_encryption_not_configured") {
      throw new Error(
        "Defina MIBOARD_SETTINGS_ENCRYPTION_KEY no ambiente da API antes de salvar a chave do MiMail.",
      );
    }
    throw new Error(
      payload?.error || `Erro administrativo (${response.status}).`,
    );
  }
  return response.status === 204 ? (undefined as T) : response.json();
};
export type AdminUser = {
  id: string;
  email: string;
  displayName: string;
  role: "admin" | "member";
  status: "active" | "disabled";
  createdAt: string;
  updatedAt?: string;
};
export type AdminPlan = {
  id: string;
  code: string;
  name: string;
  description: string;
  monthlyPriceCents: number;
  annualPriceCents: number;
  features: string[];
  active: boolean;
};
export type AdminDashboard = {
  active_users: number;
  disabled_users: number;
  boards: number;
  active_subscriptions: number;
  premium_subscriptions: number;
};
export type AdminCoupon = {
  id: string;
  code: string;
  discountType: "percent" | "fixed";
  discountValue: number;
  maxUses: number | null;
  usesCount: number;
  expiresAt: string | null;
  active: boolean;
};
export type AdminSetting = {
  key: string;
  value: Record<string, unknown>;
  updatedAt: string;
};
export type AdminSettingFieldOption = {
  label: string;
  value: string;
};
export type AdminSettingField = {
  name: string;
  label: string;
  type: "text" | "textarea" | "number" | "boolean" | "select";
  inputType?: "text" | "password" | "email" | "url";
  placeholder?: string;
  rows?: number;
  defaultValue?: string | number | boolean | string[];
  options?: AdminSettingFieldOption[];
};
export type AdminSettingPage = {
  key: string;
  title: string;
  description: string;
  fields: AdminSettingField[];
};
export type AdminLog = {
  id: string;
  level: string;
  message: string;
  metadata: Record<string, unknown>;
  createdAt: string;
};
export type AdminOrder = {
  id: string;
  amountCents: number;
  status: string;
  provider: string | null;
  createdAt: string;
  email: string | null;
  planName: string | null;
};
export type AdminCart = {
  id: string;
  status: string;
  items: unknown[];
  updatedAt: string;
  email: string | null;
};
export const listAdminUsers = async () =>
  (await request<{ users: AdminUser[] }>("/api/admin/users")).users;
export const createAdminUser = (user: {
  email: string;
  displayName: string;
  password: string;
  role?: "admin" | "member";
  status?: "active" | "disabled";
}) =>
  request<{ user: AdminUser }>("/api/admin/users", {
    method: "POST",
    body: JSON.stringify(user),
  });
export const updateAdminUser = (
  id: string,
  changes: Partial<
    Pick<AdminUser, "displayName" | "role" | "status"> & { password: string }
  >,
) =>
  request<{ user: AdminUser }>(`/api/admin/users/${id}`, {
    method: "PATCH",
    body: JSON.stringify(changes),
  });
export const disableAdminUser = (id: string) =>
  request<void>(`/api/admin/users/${id}`, { method: "DELETE" });
export const listAdminPlans = async () =>
  (await request<{ plans: AdminPlan[] }>("/api/admin/plans")).plans;
export const createAdminPlan = (
  plan: Omit<AdminPlan, "id"> & { active?: boolean },
) =>
  request<{ plan: AdminPlan }>("/api/admin/plans", {
    method: "POST",
    body: JSON.stringify(plan),
  });
export const updateAdminPlan = (
  id: string,
  changes: Partial<Omit<AdminPlan, "id" | "code">>,
) =>
  request<{ plan: AdminPlan }>(`/api/admin/plans/${id}`, {
    method: "PATCH",
    body: JSON.stringify(changes),
  });
export const getAdminDashboard = async () =>
  (await request<{ dashboard: AdminDashboard }>("/api/admin/dashboard"))
    .dashboard;
export const listAdminCoupons = async () =>
  (await request<{ coupons: AdminCoupon[] }>("/api/admin/coupons")).coupons;
export const createAdminCoupon = (coupon: {
  code: string;
  discountType: "percent" | "fixed";
  discountValue: number;
  maxUses?: number | null;
  expiresAt?: string | null;
  active?: boolean;
}) =>
  request<{ coupon: AdminCoupon }>("/api/admin/coupons", {
    method: "POST",
    body: JSON.stringify(coupon),
  });
export const updateAdminCoupon = (
  id: string,
  changes: Partial<
    Pick<
      AdminCoupon,
      | "code"
      | "discountType"
      | "discountValue"
      | "maxUses"
      | "expiresAt"
      | "active"
    >
  >,
) =>
  request<{ coupon: AdminCoupon }>(`/api/admin/coupons/${id}`, {
    method: "PATCH",
    body: JSON.stringify(changes),
  });
export const listAdminSettings = async () =>
  (await request<{ settings: AdminSetting[] }>("/api/admin/settings")).settings;
export const listAdminSettingPages = async () =>
  (
    await request<{
      pages: AdminSettingPage[];
    }>("/api/admin/settings/pages")
  ).pages;
export const saveAdminSetting = (key: string, value: Record<string, unknown>) =>
  request<{ setting: AdminSetting }>("/api/admin/settings", {
    method: "PATCH",
    body: JSON.stringify({ key, value }),
  });
export const testAdminEmailIntegration = (recipientEmail?: string) =>
  request<{ ok: boolean; result: Record<string, unknown> }>(
    "/api/admin/email/test",
    {
      method: "POST",
      body: JSON.stringify({ recipientEmail }),
    },
  );
export const listAdminLogs = async () =>
  (await request<{ logs: AdminLog[] }>("/api/admin/logs")).logs;
export const listAdminOrders = async () =>
  (await request<{ orders: AdminOrder[] }>("/api/admin/orders")).orders;
export const listAdminCarts = async () =>
  (await request<{ carts: AdminCart[] }>("/api/admin/carts")).carts;
